<template>
  <a-card
    id="auto-review-container"
    :body-style="{ padding: '16px' }"
  >
    <template #title>
      <div class="d-flex justify-content-between align-items-center">
        <h6 class="my-auto">
          <strong>{{ taskName }}</strong>
        </h6>
        <a-space>
          <span>Enable auto-review</span>
          <a-switch
            id="auto-review-toggle"
            :checked="enabled"
            :disabled="isUpdatingReviewStartDate"
            @change="handleToggle"
          />
        </a-space>
      </div>
    </template>

    <a-alert
      type="info"
      show-icon
      :message="tooltipMessage"
      class="mb-3"
    />

    <div v-if="enabled" class="d-flex flex-column">
      <label class="mb-1">
        <strong>Auto-review since</strong>
        <a-tooltip :title="tooltipMessage" placement="right">
          <question-circle-outlined class="ml-1 text-secondary" />
        </a-tooltip>
      </label>
      <a-space>
        <a-date-picker
          id="auto-review-date-picker"
          v-model:value="localDate"
          :show-time="{ format: 'HH:mm' }"
          format="YYYY-MM-DD HH:mm"
          :allow-clear="false"
          :disabled="isUpdatingReviewStartDate"
          style="min-width: 240px"
        />
        <a-button
          id="auto-review-clear-btn"
          :disabled="!localDate || isUpdatingReviewStartDate"
          @click="handleClearDate"
        >
          Clear
        </a-button>
      </a-space>
      <small class="text-secondary mt-1">
        Timezone: {{ timezone }}
      </small>
    </div>

    <div class="d-flex pt-3">
      <a-space class="ml-auto">
        <a-button
          id="auto-review-cancel-btn"
          :disabled="isUpdatingReviewStartDate"
          @click="updateAutoReviewModalState(false)"
        >
          Cancel
        </a-button>
        <a-button
          id="auto-review-save-btn"
          type="primary"
          :loading="isUpdatingReviewStartDate"
          @click="handleSave"
        >
          Save
        </a-button>
      </a-space>
    </div>
  </a-card>
</template>

<script>
import { QuestionCircleOutlined } from '@ant-design/icons-vue';
import dayjs from 'dayjs';
import { mapActions, mapGetters } from 'vuex';

export default {
  components: { QuestionCircleOutlined },
  inject: ['toast'],
  data() {
    return {
      enabled: false,
      localDate: null,
      tooltipMessage:
        'Cycles recorded on or after this time will be automatically reviewed by our VLM at 12 AM PT each night. Leave empty to disable auto-review for this task.',
    };
  },
  computed: {
    ...mapGetters([
      'taskName',
      'reviewStartDate',
      'isUpdatingReviewStartDate',
    ]),
    timezone() {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    },
  },
  mounted() {
    if (this.reviewStartDate) {
      this.enabled = true;
      this.localDate = dayjs(this.reviewStartDate);
    } else {
      this.enabled = false;
      this.localDate = null;
    }
  },
  methods: {
    ...mapActions([
      'updateAutoReviewModalState',
      'updateReviewStartDate',
    ]),

    handleToggle(checked) {
      this.enabled = checked;
      if (checked && !this.localDate) {
        this.localDate = dayjs();
      }
    },

    handleClearDate() {
      this.localDate = null;
    },

    async handleSave() {
      let payload = null;
      if (this.enabled) {
        if (!this.localDate) {
          this.toast.info('Please select a date to enable auto-review.');
          return;
        }
        payload = this.localDate.toISOString();
      }
      const { error } = await this.updateReviewStartDate(payload);
      if (error) {
        this.toast.error('Failed to update auto-review settings.');
        return;
      }
      this.toast.success(
        payload ? 'Auto-review enabled.' : 'Auto-review disabled.'
      );
    },
  },
};
</script>
